import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ordersLink, cardLinkProps } from '../../utilities/orderDeepLinks';
import useOrderAlerts from '../../hooks/useOrderAlerts';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Table,
    Badge,
    Spinner,
    Button,
    OverlayTrigger,
    Tooltip,
} from 'react-bootstrap';
import { MdVolumeUp, MdVolumeOff } from 'react-icons/md';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getUserFromLocalStorage } from '../../utilities/user';
import socket from '../../utilities/socket';
import PartnerDeliveryEarnings from '../../components/money/PartnerDeliveryEarnings';
import notificationSound from '../../assets/img/newOrderNotification.mp3';
import recallSound from '../../assets/img/restaurant_notification_beep.mp3';
import { colors, font, text, cardBody, pill } from './partnerTheme';

const API_URL = process.env.REACT_APP_SERVER_URL || '';

/** A full-width colour band that clearly separates each summary card. */
const SectionHeader = ({ title, accent, right }) => (
    <CardHeader
        style={{
            background: accent,
            border: 'none',
            padding: '18px 28px',
        }}
    >
        <div className="d-flex justify-content-between align-items-center">
            <CardTitle
                tag="h5"
                className="mb-0"
                style={{ ...text.title, color: colors.white }}
            >
                {title}
            </CardTitle>
            {right}
        </div>
    </CardHeader>
);

/**
 * One figure and its label. The figure is the hero: mono and tabular so a
 * column of them lines up, with the label stepped back to muted.
 */
const StatLine = ({ label, value, valueColor, last }) => (
    <div
        className="d-flex justify-content-between align-items-baseline"
        style={{ padding: '11px 0', borderBottom: last ? 'none' : `1px solid ${colors.lineSoft}` }}
    >
        <span style={{ fontFamily: font.sans, fontSize: 13, color: colors.muted }}>{label}</span>
        <span
            style={{
                fontFamily: font.mono,
                fontVariantNumeric: 'tabular-nums',
                fontSize: 19,
                fontWeight: 600,
                color: valueColor || colors.ink,
                whiteSpace: 'nowrap',
            }}
        >
            {value}
        </span>
    </div>
);

/** A stage of the live queue: hue dot, name, and the count as a shared pill. */
const ChipLine = ({ label, dot, tone, count, last }) => (
    <div
        className="d-flex justify-content-between align-items-center"
        style={{ padding: '10px 0', borderBottom: last ? 'none' : `1px solid ${colors.lineSoft}` }}
    >
        <span style={{ fontFamily: font.sans, fontSize: 13, color: colors.body, display: 'inline-flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: dot, flexShrink: 0 }} />
            {label}
        </span>
        <span
            style={{
                ...pill(tone),
                fontFamily: font.mono,
                fontSize: 12,
                fontVariantNumeric: 'tabular-nums',
                minWidth: 40,
                justifyContent: 'center',
            }}
        >
            {count}
        </span>
    </div>
);

const PartnerDashboard = () => {
    const history = useHistory();
    // Use agent from auth state (not authData)
    const { agent } = useSelector((state) => state.auth);
    // Fallback to localStorage if agent not in redux
    const user = agent || getUserFromLocalStorage();
    const partnerId = user?.result?.partnerId || user?.result?._id;
    const companyName = user?.result?.companyName || 'Partner';
    const token = user?.token;

    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [riders, setRiders] = useState([]);
    const [error, setError] = useState('');

    // Shared, reliable alert engine — one instance per sound. The assignment
    // beep also drives the hidden-tab desktop notification + Web Push; the recall
    // beep is a plain sound. Both replace the old AudioContext + useSound code.
    const orderAlerts = useOrderAlerts({ soundUrl: notificationSound });
    const recallAlerts = useOrderAlerts({ soundUrl: recallSound });

    const [audioEnabled, setAudioEnabled] = useState(() => {
        const saved = localStorage.getItem('3pl_audio_enabled');
        return saved === null ? true : saved === 'true';
    });

    // audioReady mirrors whether alerts are enabled (the hook auto-unlocks audio
    // on the first user gesture, so there is no separate "suspended" state to poll).
    const audioReady = orderAlerts.enabled;

    // Stable refs so the long-lived socket effect never re-subscribes on re-render.
    const notifyRef = useRef(orderAlerts.notify);
    notifyRef.current = orderAlerts.notify;
    const recallPlayRef = useRef(recallAlerts.play);
    recallPlayRef.current = recallAlerts.play;

    // Persist audio preference
    useEffect(() => {
        localStorage.setItem('3pl_audio_enabled', String(audioEnabled));
    }, [audioEnabled]);

    // Stable play function using useCallback — routes through the shared engine.
    const playSound = useCallback((type) => {
        if (!audioEnabled) return;

        if (type === 'recall') {
            recallPlayRef.current();
        } else {
            notifyRef.current({
                title: '📦 New order assigned',
                body: 'A new order was assigned to your team. Tap to view.',
                url: '/admin/partner-orders',
                tag: 'new-order',
            });
        }
    }, [audioEnabled]);

    // Toggle audio on/off — drives both shared engines plus this view's UI state.
    const toggleAudio = useCallback(() => {
        const newState = !audioEnabled;
        setAudioEnabled(newState);

        if (newState) {
            orderAlerts.enable();
            recallAlerts.enable({ silent: true }); // avoid a second confirmation tone
            toast.success('Audio notifications enabled');
        } else {
            orderAlerts.disable();
            recallAlerts.disable();
            toast.info('Audio notifications disabled');
        }
    }, [audioEnabled, orderAlerts, recallAlerts]);

    useEffect(() => {
        if (partnerId && token) {
            fetchDashboardData();
            fetchRiders();
        } else {
            setLoading(false);
            setError('Partner ID not found. Please log in again.');
        }
    }, [partnerId, token]);

    // Socket listener for real-time rider status updates
    useEffect(() => {
        if (!partnerId) return;

        let isMounted = true;

        // Join partner room for real-time updates
        socket.emit('join3PLPartnerRoom', partnerId);

        // Listen for rider status changes
        const handleRiderStatusChange = ({ riderId, status, isAvailable }) => {
            setRiders(prevRiders =>
                prevRiders.map(rider =>
                    rider._id === riderId
                        ? { ...rider, onlineStatus: status, availability: { ...rider.availability, isAvailable } }
                        : rider
                )
            );
        };

        // Listen for global rider status updates
        const handleGlobalRiderStatusUpdate = ({ riderId, status }) => {
            setRiders(prevRiders =>
                prevRiders.map(rider =>
                    rider._id === riderId
                        ? { ...rider, onlineStatus: status, availability: { ...rider.availability, isAvailable: status === 'Online' } }
                        : rider
                )
            );
        };

        // Listen for new orders assigned to this partner
        const handleOrderAssignedTo3PL = ({ orderId, order, message }) => {
            // Refresh dashboard data to show updated order counts
            if (isMounted) fetchDashboardData();
            playSound('assign');
        };

        // Listen for orders recalled by vendor
        const handleOrderRecalledFrom3PL = ({ orderId, message }) => {
            // Refresh dashboard data to show updated order counts
            if (isMounted) fetchDashboardData();
            playSound('recall');
        };

        // Listen for order status changes (rider accepts, picks up, delivers, etc.)
        // This keeps the Active Orders card in sync in real time
        const handleOrderStatusChange = (data) => {
            if (isMounted) {
                fetchDashboardData();
                // Notify the partner of what changed
                const status = data?.status?.toUpperCase();
                const statusMessages = {
                    'ACCEPTED': '✅ Rider accepted an order',
                    'PICKED_UP': '🎒 Order picked up by rider',
                    'DISPATCHED': '🚚 Rider is on the way to customer',
                    'DELIVERED': '📦 Order delivered successfully',
                    'REJECTED': '❌ Rider rejected an order',
                };
                const message = statusMessages[status];
                if (message) {
                    toast.info(message, { toastId: `status-${data?.orderId}-${status}` });
                }
            }
        };

        socket.on('rider:statusChanged', handleRiderStatusChange);
        socket.on('riderStatusUpdate', handleGlobalRiderStatusUpdate);
        socket.on('order:assignedTo3PL', handleOrderAssignedTo3PL);
        socket.on('order:recalledFrom3PL', handleOrderRecalledFrom3PL);
        socket.on('order:status_changed', handleOrderStatusChange);
        socket.on('order:accepted', handleOrderStatusChange);
        socket.on('order:completed', handleOrderStatusChange);

        // Re-sync on socket reconnection (e.g. tab wakeup, network blip)
        const handleReconnect = () => {
            socket.emit('join3PLPartnerRoom', partnerId);
            if (isMounted) {
                fetchDashboardData();
                fetchRiders();
            }
        };
        socket.on('connect', handleReconnect);

        return () => {
            isMounted = false;
            socket.off('rider:statusChanged', handleRiderStatusChange);
            socket.off('riderStatusUpdate', handleGlobalRiderStatusUpdate);
            socket.off('order:assignedTo3PL', handleOrderAssignedTo3PL);
            socket.off('order:recalledFrom3PL', handleOrderRecalledFrom3PL);
            socket.off('order:status_changed', handleOrderStatusChange);
            socket.off('order:accepted', handleOrderStatusChange);
            socket.off('order:completed', handleOrderStatusChange);
            socket.off('connect', handleReconnect);
            socket.emit('leave3PLPartnerRoom', partnerId);
        };
    }, [partnerId, playSound]);

    const fetchDashboardData = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}/dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setDashboardData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching dashboard:', err);
            // Don't show error, just use default values
            setDashboardData({
                today: { orders: 0, completed: 0, revenue: 0 },
                thisWeek: { orders: 0, completed: 0, averageDeliveryTime: 0, revenue: 0 },
                thisMonth: { orders: 0, revenue: 0 },
                riders: { active: 0, total: 0 },
                orders: { pending: 0, inTransit: 0 },
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchRiders = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}/riders`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setRiders(response.data.data?.riders || []);
            }
        } catch (err) {
            console.error('Error fetching riders:', err);
            setRiders([]);
        }
    };

    if (loading) {
        return (
            <div className="content" style={{ padding: '20px' }}>
                <div
                    className="d-flex align-items-center"
                    style={{ gap: 10, marginBottom: 18, color: colors.muted, fontSize: 14 }}
                    role="status"
                    aria-live="polite"
                >
                    <Spinner animation="grow" size="sm" style={{ color: "#018022ff" }} />
                    <span>Loading your delivery performance</span>
                </div>
                {/* Placeholders that match the real cards' geometry, so the page
                    does not jump when the numbers land and the partner can see
                    the shape of what is coming rather than an empty screen. */}
                <Row>
                    {[0, 1, 2, 3].map((i) => (
                        <Col lg="3" sm="6" key={i}>
                            <Card className="card-stats">
                                <CardBody style={{ minHeight: 96 }}>
                                    <div
                                        style={{
                                            height: 12,
                                            width: "55%",
                                            borderRadius: 6,
                                            background: colors.lineSoft,
                                            marginBottom: 12,
                                        }}
                                    />
                                    <div
                                        style={{
                                            height: 22,
                                            width: "38%",
                                            borderRadius: 6,
                                            background: colors.lineSoft,
                                        }}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    }

    /* `to` makes the whole card a link, keyboard included (utilities/
       orderDeepLinks.js). Each card opens the page whose figure it shows: the
       orders page on the tab it counted, the riders page, the earnings page. */
    const StatCard = ({ title, value, subtitle, icon, color = colors.blue, to, label }) => (
        <Card
            className="card-stats"
            style={to ? { cursor: 'pointer' } : undefined}
            {...(to ? cardLinkProps(() => history.push(to), label || `Open ${title}`) : {})}
        >
            <CardBody>
                <Row>
                    <Col xs="5">
                        <div className="icon-big text-center" style={{ color }}>
                            <i className={icon} />
                        </div>
                    </Col>
                    <Col xs="7">
                        <div className="numbers">
                            <p className="card-category" style={{ color: colors.muted }}>{title}</p>
                            <CardTitle tag="h3" style={{ color: colors.body }}>{value}</CardTitle>
                        </div>
                    </Col>
                </Row>
                {subtitle && (
                    <>
                        <hr />
                        <div className="stats" style={{ color: colors.muted }}>
                            {subtitle}
                        </div>
                    </>
                )}
            </CardBody>
        </Card>
    );

    return (
        <div className="content" style={{ marginTop: '-50px' }}>
            {/* Welcome Header */}
            <Row className="mb-4">
                <Col>
                    <h4 style={{ color: colors.body }}>
                        Welcome back, <span style={{ color: colors.blue, fontWeight: 'bold' }}>{companyName}</span>
                    </h4>
                    <p style={{ color: colors.muted }}>Here's your delivery performance overview</p>
                </Col>
                <Col className="text-end">
                    <OverlayTrigger
                        placement="bottom"
                        overlay={
                            <Tooltip>
                                {audioEnabled
                                    ? (audioReady
                                        ? 'Audio notifications are ON'
                                        : 'Click to activate audio (browser requires interaction)')
                                    : 'Audio notifications are OFF'}
                            </Tooltip>
                        }
                    >
                        <Button
                            variant={audioEnabled ? (audioReady ? 'success' : 'warning') : 'outline-secondary'}
                            onClick={toggleAudio}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                borderRadius: '8px',
                                marginLeft: 'auto',
                                animation: audioEnabled && !audioReady ? 'pulse 2s infinite' : 'none'
                            }}
                        >
                            {audioEnabled ? (
                                <MdVolumeUp size={20} />
                            ) : (
                                <MdVolumeOff size={20} />
                            )}
                            {audioEnabled
                                ? (audioReady ? 'Alert On' : 'Click to Activate')
                                : 'Alert Off'}
                        </Button>
                    </OverlayTrigger>
                </Col>
            </Row>

            {error && (
                <div className="alert alert-danger">{error}</div>
            )}

            {/* Stats Cards */}
            <Row>
                <Col lg="3" sm="6">
                    <StatCard
                        title="Today's Orders"
                        value={dashboardData?.today?.orders || 0}
                        icon="nc-icon nc-cart-simple"
                        color={colors.blue}
                        to={ordersLink('partner', { tab: 'all' })}
                        label="Open today's orders"
                    />
                </Col>
                <Col lg="3" sm="6">
                    <StatCard
                        title="Completed Today"
                        value={dashboardData?.today?.completed || 0}
                        icon="nc-icon nc-check-2"
                        color={colors.green}
                        to={ordersLink('partner', { tab: 'completed' })}
                        label="Open the orders completed today"
                    />
                </Col>
                <Col lg="3" sm="6">
                    <StatCard
                        title="Active Riders"
                        value={`${dashboardData?.riders?.active || 0} / ${dashboardData?.riders?.total || 0}`}
                        icon="nc-icon nc-delivery-fast"
                        color="#f59e0b"
                        to="/admin/partner-riders"
                        label="Open my riders"
                    />
                </Col>
                <Col lg="3" sm="6">
                    <StatCard
                        title="Today's Revenue"
                        value={`UGX ${(dashboardData?.today?.revenue || 0).toLocaleString()}`}
                        icon="nc-icon nc-money-coins"
                        color="#10b981"
                        to="/admin/partner-earnings"
                        label="Open delivery earnings"
                    />
                </Col>
            </Row>

            {/* Delivery earnings: the same order economics the platform reports,
                scoped by the server to this partner's own deliveries. */}
            <Row className="mt-4">
                <Col xs="12">
                    <PartnerDeliveryEarnings partnerId={partnerId} />
                </Col>
            </Row>

            {/* Weekly Stats */}
            <Row className="mt-4">
                <Col lg="4" sm="6">
                    <Card style={{ borderColor: colors.line }}>
                        <SectionHeader title="This Week" accent={colors.blue} />
                        <CardBody style={{ ...cardBody, paddingTop: 6, paddingBottom: 16 }}>
                            <StatLine label="Total Orders" value={dashboardData?.thisWeek?.orders || 0} />
                            <StatLine label="Completed" value={dashboardData?.thisWeek?.completed || 0} />
                            <StatLine
                                label="Avg. Delivery Time"
                                value={`${dashboardData?.thisWeek?.averageDeliveryTime || 0} mins`}
                            />
                            <StatLine
                                label="Revenue"
                                value={`UGX ${(dashboardData?.thisWeek?.revenue || 0).toLocaleString()}`}
                                valueColor={colors.green}
                                last
                            />
                        </CardBody>
                    </Card>
                </Col>

                <Col lg="4" sm="6">
                    <Card style={{ borderColor: colors.line }}>
                        <SectionHeader title="This Month" accent={colors.green} />
                        <CardBody style={{ ...cardBody, paddingTop: 6, paddingBottom: 16 }}>
                            <StatLine label="Total Orders" value={dashboardData?.thisMonth?.orders || 0} />
                            <StatLine
                                label="Revenue"
                                value={`UGX ${(dashboardData?.thisMonth?.revenue || 0).toLocaleString()}`}
                                valueColor={colors.green}
                                last
                            />
                        </CardBody>
                    </Card>
                </Col>

                <Col lg="4" sm="6">
                    <Card style={{ borderColor: colors.line }}>
                        <SectionHeader
                            title="Active Orders"
                            accent="#f59e0b"
                            right={
                                <span
                                    style={{
                                        ...pill('muted'),
                                        background: colors.white,
                                        color: colors.ink,
                                        fontFamily: font.mono,
                                        fontSize: 12,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {(dashboardData?.orders?.newAssigned || 0) +
                                        Math.max(0, (dashboardData?.orders?.pending || 0) - (dashboardData?.orders?.newAssigned || 0)) +
                                        (dashboardData?.orders?.inTransit || 0)}
                                </span>
                            }
                        />
                        <CardBody style={{ ...cardBody, paddingTop: 6, paddingBottom: 16 }}>
                            {/* Awaiting Rider, PENDING_3PL: assigned to partner, no rider yet */}
                            <ChipLine
                                label="Awaiting Rider"
                                dot={colors.red}
                                tone="bad"
                                count={dashboardData?.orders?.newAssigned || 0}
                            />
                            {/* Assigned to Rider, ASSIGNED: rider notified, waiting for accept/reject */}
                            <ChipLine
                                label="Assigned to Rider"
                                dot={colors.amber}
                                tone="warn"
                                count={Math.max(0, (dashboardData?.orders?.pending || 0) - (dashboardData?.orders?.newAssigned || 0))}
                            />
                            {/* Rider Accepted, ACCEPTED: rider accepted, heading to vendor */}
                            <ChipLine
                                label="Rider Accepted"
                                dot={colors.amber}
                                tone="warn"
                                count={dashboardData?.orders?.accepted || 0}
                            />
                            {/* Picked Up, PICKED_UP: rider has picked up items from vendor */}
                            <ChipLine
                                label="Picked Up"
                                dot={colors.teal}
                                tone="info"
                                count={dashboardData?.orders?.pickedUp || 0}
                            />
                            {/* On the Way, DISPATCHED: rider in transit to customer */}
                            <ChipLine
                                label="On the Way"
                                dot={colors.teal}
                                tone="info"
                                count={dashboardData?.orders?.onTheWay || 0}
                                last
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Riders Table */}
            <Row className="mt-2">
                <Col>
                    <Card className="mb-2">
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <CardTitle tag="h5" style={{ color: colors.body, marginBottom: 0 }}>
                                Your Riders
                            </CardTitle>
                            <Button
                                size="sm"
                                style={{ backgroundColor: colors.blue, borderColor: colors.blue }}
                                onClick={() => history.push('/admin/partner-riders')}
                            >
                                View All
                            </Button>
                        </CardHeader>
                        <CardBody style={{ padding: '0.5rem 1rem' }}>
                            {riders.length === 0 ? (
                                <p className="text-center text-muted">No riders assigned yet</p>
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Phone</th>
                                            <th>Status</th>
                                            <th>Deliveries</th>
                                            <th>Rating</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {riders.slice(0, 5).map((rider) => (
                                            <tr key={rider._id}>
                                                <td>{rider.name || `${rider.firstName} ${rider.lastName}`}</td>
                                                <td>{rider.mobile}</td>
                                                <td>
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        padding: '3px 10px',
                                                        borderRadius: '20px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        letterSpacing: '0.2px',
                                                        textTransform: 'uppercase',
                                                        backgroundColor: rider.onlineStatus === 'Online' ? '#ecfdf5' : '#f3f4f6',
                                                        color: rider.onlineStatus === 'Online' ? '#065f46' : '#6b7280',
                                                        border: `1.5px solid ${rider.onlineStatus === 'Online' ? '#07a617ff' : '#d1d5db'}`,
                                                    }}>
                                                        <span style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            backgroundColor: rider.onlineStatus === 'Online' ? '#07a617ff' : '#9ca3af',
                                                            boxShadow: rider.onlineStatus === 'Online' ? '0 0 0 3px rgba(16,185,129,0.25)' : 'none',
                                                            animation: rider.onlineStatus === 'Online' ? 'statusPulse 2s ease-in-out infinite' : 'none',
                                                        }} />
                                                        {rider.onlineStatus}
                                                    </span>
                                                </td>
                                                <td>{rider.performanceMetrics?.totalDeliveries || 0}</td>
                                                <td>
                                                    {rider.performanceMetrics?.averageRating?.toFixed(1) || 'N/A'}
                                                    {rider.performanceMetrics?.averageRating && ' ⭐'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Pulse animation for online status */}
            <style>{`
                @keyframes statusPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.6; transform: scale(1.3); }
                }
            `}</style>

            {/* Top Performers */}
            {dashboardData?.topRiders?.length > 0 && (
                <Row className="mt-2">
                    <Col>
                        <Card className="mb-2">
                            <CardHeader>
                                <CardTitle tag="h5" style={{ color: colors.body }}>
                                    🏆 Top Performing Riders
                                </CardTitle>
                            </CardHeader>
                            <CardBody style={{ padding: '0.5rem 1rem' }}>
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Rank</th>
                                            <th>Name</th>
                                            <th>Orders this month</th>
                                            <th>Deliveries</th>
                                            <th>Rating</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dashboardData.topRiders.map((rider, index) => (
                                            <tr key={rider._id}>
                                                <td>
                                                    <Badge bg={index === 0 ? 'warning' : index === 1 ? 'secondary' : 'info'}>
                                                        #{index + 1}
                                                    </Badge>
                                                </td>
                                                <td>{rider.name || `${rider.firstName} ${rider.lastName}`}</td>
                                                <td>{rider.totalOrders || 0}</td>
                                                <td>{rider.performanceMetrics?.totalDeliveries || 0}</td>
                                                <td>{rider.performanceMetrics?.averageRating?.toFixed(1) || 'N/A'} ⭐</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default PartnerDashboard;
